import { useQuery } from "@tanstack/react-query";
import { useEndpoints } from "../../api/ApiContext";
import type { PromotionalEarnings } from "../../api/types";
import { Card, Money, MoneyStat, SkeletonBlock, Table } from "../../components/ui";
import { formatLocal } from "../../lib/datetime";
import { isZeroDecimal } from "../../lib/money";

// Migration 0075 (promotions.go, GET /v1/earnings/promotional): the
// publisher's share of impressions bought with VADS promotional credit.
// Never part of the payable balance, never counted toward the payout
// minimum; paid only when VADS funds its promotional payout pool, at no
// fixed time. A converted amount becomes ordinary earnings (the usual
// hold and minimum apply), so it shows up in the balances above instead.

export function PromotionalEarningsCard({ compact = false }: { compact?: boolean }) {
  const ep = useEndpoints();
  const q = useQuery({ queryKey: ["earnings", "promotional"], queryFn: () => ep.publisher.getPromotionalEarnings(), retry: false });
  const title = "Promotional earnings (paid when VADS funds its promo pool)";
  if (q.isError) {
    return (
      <Card title={title}>
        <p className="small muted">Promotional earnings couldn't be loaded right now.</p>
      </Card>
    );
  }
  const e: PromotionalEarnings | undefined = q.data;
  return (
    <Card
      title={title}
      subtitle="Your share of ads paid for with VADS promotional credit. Separate from your payable earnings: not included in payouts and not counted toward the payout minimum."
    >
      {!e ? (
        <SkeletonBlock label="Loading promotional earnings" lines={2} />
      ) : (
        <div className="stack">
          <div className="grid-auto">
            <MoneyStat label="Promotional earnings" value={e.outstanding_total} currency={e.currency} note="Not payable until VADS funds its promotional pool" />
            {!compact ? (
              <>
                <MoneyStat label="Past the review window" value={e.awaiting_pool_total} currency={e.currency} note="What a pool funding can convert" />
                <MoneyStat label="Converted so far" value={e.converted_total} currency={e.currency} note="Now ordinary earnings in your balances" />
              </>
            ) : null}
          </div>
          <p className="small">{e.explanation}</p>
          {!compact && e.conversions.length ? (
            <Table
              caption="Promotional pool conversions"
              rows={e.conversions}
              rowKey={(c) => c.funding_id}
              columns={[
                { key: "at", header: "Converted", cell: (c) => formatLocal(c.converted_at) },
                { key: "amt", header: "Amount", numeric: true, cell: (c) => <Money value={c.amount} currency={e.currency} /> },
              ]}
            />
          ) : null}
          {compact && !isZeroDecimal(e.outstanding_total) ? (
            <p className="xs muted">These don't count toward the minimum payout amount.</p>
          ) : null}
        </div>
      )}
    </Card>
  );
}
